/**
 * Rule helpers and composable field sets for the visibility registry.
 * Re-exported by `view-fields.ts`; import from there.
 */

import type {
  Brand,
  FieldRule,
  MediaTypeKey,
  ProfileFieldKey,
  ViewContext,
} from './view-fields.types';

/** Always visible. */
export const ALWAYS: FieldRule = () => true;

/** Never visible (used to make the table self-documenting). */
export const NEVER: FieldRule = () => false;

/** Owner or super admin. */
export const ONLY_OWNER: FieldRule = (ctx) =>
  ctx.viewer === 'owner' || ctx.viewer === 'admin';

/** Owner, super admin, or any recruiter (client or organization member). */
export const OWNER_OR_RECRUITER: FieldRule = (ctx) =>
  ctx.viewer === 'owner' ||
  ctx.viewer === 'admin' ||
  ctx.viewer === 'recruiter';

/** Authenticated viewers only (any logged-in user). */
export const AUTHENTICATED_ONLY: FieldRule = (ctx) =>
  ctx.viewer !== 'anonymous';

/**
 * Fever staff or super admin ONLY. The gate for cast-signal fields on the
 * fever_directory surface (BAL-316, R4). Every other viewer — including a
 * dancer peer, an owner, or the anonymous public — is denied. This is the
 * registry half of the "no peer cast count anywhere" lock; the data half is
 * the self-gated get_fever_cast_metrics() RPC.
 */
export const FEVER_OR_ADMIN: FieldRule = (ctx) =>
  ctx.viewer === 'fever_staff' || ctx.viewer === 'admin';

/** Restrict to a specific brand. */
export const onlyOnBrand =
  (brand: Brand): FieldRule =>
  (ctx) =>
    ctx.brand === brand;

/** Boolean OR of multiple rules. */
export const anyOf =
  (...rules: FieldRule[]): FieldRule =>
  (ctx) =>
    rules.some((r) => r(ctx));

/**
 * Default context used by the legacy helpers (`isFieldVisibleInView`,
 * `getFieldsForView`). Picks the most-restrictive viewer (`anonymous`)
 * on Flow, which matches the original "is this in the set?" behavior for
 * fields that were always-public.
 */
export const DEFAULT_CTX: Pick<ViewContext, 'brand' | 'viewer'> = {
  brand: 'flow',
  viewer: 'anonymous',
};

// ==================== Field sets (composable) ====================

export const IDENTITY_FIELDS: ProfileFieldKey[] = [
  'firstName',
  'lastName',
  'profilePhotoUrl',
  'gender',
  'birthdate',
  'nationalities',
  'languages',
];

export const LOCATION_FIELDS: ProfileFieldKey[] = [
  'city',
  'countryOfResidence',
];

export const PROFESSIONAL_FIELDS: ProfileFieldKey[] = [
  'bio',
  'yearsOfExperience',
  'danceStyles',
  'danceLevel',
  'availableForHire',
  'lookingFor',
];

export const DIMENSION_FIELDS: ProfileFieldKey[] = [
  'heightCm',
  'weightKg',
  'bustCm',
  'waistCm',
  'hipsCm',
  'clothingSize',
  'shoeSize',
  'pointeShoeBrand',
  'pointeShoeSize',
];

export const APPEARANCE_FIELDS: ProfileFieldKey[] = [
  'eyeColor',
  'hairColor',
  'skinTone',
];

export const SOCIAL_FIELDS: ProfileFieldKey[] = [
  'instagramHandle',
  'tiktokHandle',
  'facebookUsername',
  'youtubeUsername',
  'vimeoHandle',
  'pinterestHandle',
  'snapchatHandle',
  'xHandle',
  'website',
  'portfolioUrl',
];

export const AVAILABILITY_FIELDS: ProfileFieldKey[] = [
  'geographicAvailability',
];

/**
 * Cast-signal fields — the Fever recruitment metrics. Enumerated here so the
 * registry is the single source that knows what "cast frequency data" means
 * (BAL-316, R4). The leak-guard test imports this set to prove every one of
 * them is FEVER_OR_ADMIN-gated and stripped for peer/public/self scopes.
 */
export const CAST_SIGNAL_FIELDS: ProfileFieldKey[] = [
  'totalFeverShows',
  'lastCastDate',
  'availabilityNext30d',
  'distanceFromTarget',
  'profileCompleteness',
];

export const ALL_PUBLIC_MEDIA: MediaTypeKey[] = [
  'headshot',
  'photos',
  'videos',
  'arabesque',
  'balletClass',
  'classicalVariation',
  'modernVariation',
];

/**
 * Build a rules map by pairing every key in `keys` with the same rule.
 * Used to keep large surface definitions readable.
 */
export function withRule<K extends string>(
  keys: readonly K[],
  rule: FieldRule,
): Partial<Record<K, FieldRule>> {
  return Object.fromEntries(keys.map((k) => [k, rule])) as Partial<
    Record<K, FieldRule>
  >;
}
